import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { DOCS_URL, isExternal } from "@/lib/site";

const ITEMS = [
  {
    title: "Sites & drillholes",
    body: "Projects and sites with a boundary, coordinate system and rock properties. Collars, surveys, geology and samples imported from CSV, with errors reported row by row, and drawn with the topography in a 3D site view.",
    to: "/features/sites-drillholes",
  },
  {
    title: "Data health & QA/QC",
    body: "Checks on collars, surveys and intervals, with control charts for standards, blanks and duplicates.",
    to: "/features/sites-drillholes",
  },
  {
    title: "Block models & pit shell",
    body: "Import a block model or estimate a simple one, read its grade-tonnage curve, and work out the pit that pays at a scenario's prices. A scoping-level shell, and labelled as one.",
    to: "/features/block-models",
  },
  {
    title: "Drill & blast analysis",
    body: "Blast geometry and drill fleet sizing for every drill you want to compare, down to drilling cost per tonne. The lowest cost option is marked.",
    to: "/features/drill-blast",
  },
  {
    title: "Mine plan, fleet & plant",
    body: "One mine plan per scenario: a constant rate, a pit schedule or a schedule you import. Equipment, plant and overheads are costed on it, with the fleet from a quick estimate or built from vendor quotes.",
    to: "/features/cost-model",
  },
  {
    title: "Projection & comparison",
    body: "A year-by-year cashflow with NPV, IRR and cost per tonne, a sensitivity chart, and every scenario of a site side by side. What could not be costed is listed, not assumed.",
    to: "/features/cost-model",
  },
  {
    title: "Reports & history",
    body: "Issue a scenario as a numbered revision, frozen with its inputs and warnings. Compare two revisions, and see who changed what in between.",
    to: "/features/reports",
  },
  {
    title: "Operations",
    body: "Record each month's tonnes, grades and costs for a site and hold them against the issued plan, by month and to date.",
    to: "/features/operations",
  },
  {
    title: "Vendors & quotes",
    body: "A register of suppliers, equipment models and quotes for the whole organization. Capital per unit and operating cost per hour are totalled when a quote is saved.",
    to: "/features/vendors-quotes",
  },
  {
    title: "Teams & API",
    body: "Four roles from viewer to admin, and an HTTP API for everything you can do in the dashboard.",
    to: `${DOCS_URL}/api-reference/overview`,
  },
];

const rowClass =
  "group relative grid gap-3 py-6 transition-[padding] duration-500 ease-out-expo md:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1.1fr)_3rem] md:items-baseline md:gap-8 md:py-7 md:hover:px-6";

export function Capabilities() {
  return (
    <section data-theme="light" className="relative bg-bg">
      <div className="shell py-20 md:py-28">
        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <div>
            <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
              What is in the box
            </p>
            <h2 data-split className="title max-w-[12ch]">
              Built for the people who run the numbers.
            </h2>
          </div>
          <p data-fade className="lede max-w-[40ch] text-mute lg:justify-self-end">
            Everything below is in the product today. Where something is still in progress, the documentation says so.
          </p>
        </div>

        <ol className="mt-10 md:mt-14">
          {ITEMS.map((item, index) => {
            const content = (
              <>
                {/* Inverted panel that wipes up on hover */}
                <span className="absolute inset-0 origin-bottom scale-y-0 bg-fg transition-transform duration-500 ease-out-expo md:group-hover:scale-y-100" />
                <span className="eyebrow relative text-mute transition-colors duration-300 md:group-hover:text-bg/60">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="subtitle relative uppercase transition-colors duration-300 md:group-hover:text-bg">
                  {item.title}
                </h3>
                <p className="relative max-w-[52ch] text-mute transition-colors duration-300 md:group-hover:text-bg/75">
                  {item.body}
                </p>
                <ArrowUpRight
                  aria-hidden="true"
                  className="relative hidden h-6 w-6 justify-self-end text-fg/40 transition-all duration-500 ease-out-expo md:block md:group-hover:translate-x-1 md:group-hover:-translate-y-1 md:group-hover:text-lime"
                />
              </>
            );
            return (
              <li key={item.title} data-fade>
                <span data-rule className="rule" />
                {isExternal(item.to) ? (
                  <a href={item.to} className={rowClass}>
                    {content}
                  </a>
                ) : (
                  <Link to={item.to} className={rowClass}>
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
        <span data-rule className="rule" />
      </div>
    </section>
  );
}
